// MCHD Room Scheduler - Failed Attempts Table Component
// Table component for displaying and managing failed login attempts

import { useState } from 'react';
import {
  Table,
  Badge,
  ActionIcon,
  Group,
  Text,
  Button,
  Modal,
  Alert,
  Stack,
  Box,
  Tooltip,
  Pagination,
  TextInput,
  Select,
  Card,
  Grid,
  Chip,
} from '@mantine/core';
import {
  IconEye,
  IconSearch,
  IconFilter,
  IconAlertCircle,
  IconClock,
  IconShield,
  IconLockOpen,
  IconBan,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { FailedAttempt } from '../../../api/endpoints/auth';
import DynamicRefreshButton from '../../atoms/DynamicRefreshButton';

interface FailedAttemptsTableProps {
  attempts: FailedAttempt[];
  isLoading?: boolean;
  onUnlockAccount: (email: string) => void;
  onUnlockIP: (ipAddress: string) => void;
  onView: (attempt: FailedAttempt) => void;
  onRefresh: () => void;
  metrics: {
    totalAttempts: number;
    unresolvedAttempts: number;
    recentAttempts: number;
    uniqueIPs: number;
    uniqueEmails: number;
    totalUsers: number;
    activeUsers: number;
    lockedAccounts: number;
    activeSessions: number;
    securityScore: number;
    securityLevel: string;
    securityColor: string;
    topOffendingIPs: Array<{ ip: string; count: number }>;
    topOffendingEmails: Array<{ email: string; count: number }>;
  };
}

/**
 * Failed attempts table component with actions and filtering
 * @param props - Component props
 * @returns JSX element
 */
export function FailedAttemptsTable({
  attempts,
  isLoading = false,
  onUnlockAccount,
  onUnlockIP,
  onView,
  onRefresh,
  metrics,
}: FailedAttemptsTableProps): JSX.Element {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [selectedAttempt, setSelectedAttempt] = useState<FailedAttempt | null>(null);
  const [unlockType, setUnlockType] = useState<'account' | 'ip'>('account');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter attempts based on search and filters
  const filteredAttempts = attempts.filter((attempt) => {
    const matchesSearch = 
      attempt.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attempt.ipAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (attempt.userAgent && attempt.userAgent.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = !statusFilter || 
      (statusFilter === 'resolved' && attempt.isResolved) ||
      (statusFilter === 'unresolved' && !attempt.isResolved);
    
    return matchesSearch && matchesStatus;
  });

  // Paginate attempts
  const totalPages = Math.ceil(filteredAttempts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAttempts = filteredAttempts.slice(startIndex, startIndex + itemsPerPage);

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getAttemptSeverity = (attempt: FailedAttempt) => {
    const attemptedAt = new Date(attempt.attemptedAt);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - attemptedAt.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 30) return { severity: 'Critical', color: 'red' };
    if (diffInMinutes < 120) return { severity: 'High', color: 'orange' };
    if (diffInMinutes < 1440) return { severity: 'Medium', color: 'yellow' };
    return { severity: 'Low', color: 'blue' };
  };

  const parseUserAgent = (userAgent: string) => {
    // Simple user agent parsing for common browsers
    if (userAgent.includes('Chrome')) return { browser: 'Chrome', icon: '🌐' };
    if (userAgent.includes('Firefox')) return { browser: 'Firefox', icon: '🦊' };
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return { browser: 'Safari', icon: '🧭' };
    if (userAgent.includes('Edge')) return { browser: 'Edge', icon: '🌐' };
    if (userAgent.includes('curl')) return { browser: 'cURL', icon: '📡' };
    if (userAgent.includes('Postman')) return { browser: 'Postman', icon: '📮' };
    return { browser: 'Unknown', icon: '❓' };
  };

  const formatResolvedBy = (resolvedBy: string | undefined) => {
    if (!resolvedBy) return 'N/A';
    if (resolvedBy === 'system') return 'System';
    return resolvedBy;
  };

  const handleUnlockClick = (attempt: FailedAttempt, type: 'account' | 'ip') => {
    setSelectedAttempt(attempt);
    setUnlockType(type);
    setUnlockModalOpen(true);
  };

  const handleUnlockConfirm = () => {
    if (selectedAttempt) {
      if (unlockType === 'account') {
        onUnlockAccount(selectedAttempt.email);
      } else {
        onUnlockIP(selectedAttempt.ipAddress);
      }
      setUnlockModalOpen(false);
      setSelectedAttempt(null);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.currentTarget.value);
    setCurrentPage(1);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <Box p="xl" ta="center">
        <Text c="dimmed">Loading failed attempts...</Text>
      </Box>
    );
  }

  return (
    <Stack gap="md">
      {/* Statistics Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">Total</Text>
              <IconAlertCircle size={16} color="var(--mantine-color-red-6)" />
            </Group>
            <Text size="lg" fw={700} c="red">{metrics.totalAttempts}</Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">Unresolved</Text>
              <IconBan size={16} color="var(--mantine-color-orange-6)" />
            </Group>
            <Text size="lg" fw={700} c="orange">{metrics.unresolvedAttempts}</Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">Recent</Text>
              <IconClock size={16} color="var(--mantine-color-blue-6)" />
            </Group>
            <Text size="lg" fw={700} c="blue">{metrics.recentAttempts}</Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">Unique IPs</Text>
              <IconShield size={16} color="var(--mantine-color-purple-6)" />
            </Group>
            <Text size="lg" fw={700} c="purple">{metrics.uniqueIPs}</Text>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
          <Card shadow="sm" padding="sm" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="xs" fw={500} c="dimmed">Unique Emails</Text>
              <IconShield size={16} color="var(--mantine-color-green-6)" />
            </Group>
            <Text size="lg" fw={700} c="green">{metrics.uniqueEmails}</Text>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Search and Filter Controls */}
      <Group justify="space-between" wrap="wrap">
        <Group gap="md" wrap="wrap">
          <TextInput
            placeholder="Search attempts..."
            leftSection={<IconSearch size={16} />}
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ minWidth: 250 }}
          />
          <Select
            placeholder="Filter by status"
            data={[
              { value: '', label: 'All Status' },
              { value: 'unresolved', label: 'Unresolved' },
              { value: 'resolved', label: 'Resolved' },
            ]}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              handleFilterChange();
            }}
            leftSection={<IconFilter size={16} />}
            clearable
          />
        </Group>
        
        <Group gap="sm">
          <DynamicRefreshButton
            variant="outline"
            onRefresh={onRefresh}
            size="sm"
            color="blue"
          />
        </Group>
      </Group>

      {/* Failed Attempts Table */}
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Email</Table.Th>
            <Table.Th>IP Address</Table.Th>
            <Table.Th>User Agent</Table.Th>
            <Table.Th>Severity</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Attempted</Table.Th>
            <Table.Th>Resolved</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {paginatedAttempts.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={8} ta="center" py="xl">
                <Stack gap="sm" align="center">
                  <IconCheck size={48} color="var(--mantine-color-green-6)" />
                  <Text c="dimmed">No failed attempts found</Text>
                  <Text size="sm" c="dimmed">
                    {searchTerm || statusFilter
                      ? 'Try adjusting your search or filters'
                      : 'Great! No security issues detected'}
                  </Text>
                </Stack>
              </Table.Td>
            </Table.Tr>
          ) : (
            paginatedAttempts.map((attempt) => {
              const severity = getAttemptSeverity(attempt);
              const userAgentInfo = parseUserAgent(attempt.userAgent || '');
              return (
                <Table.Tr key={attempt.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {attempt.email}
                    </Text>
                    {attempt.userId && (
                      <Text size="xs" c="dimmed">
                        User ID: {attempt.userId}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {attempt.ipAddress}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label={attempt.userAgent} multiline w={300}>
                      <Group gap="xs">
                        <Text size="sm">{userAgentInfo.icon}</Text>
                        <Text size="sm" truncate style={{ maxWidth: 120 }}>
                          {userAgentInfo.browser}
                        </Text>
                      </Group>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Chip
                      color={severity.color}
                      variant="light"
                      size="sm"
                    >
                      {severity.severity}
                    </Chip>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Badge
                        color={attempt.isResolved ? 'green' : 'red'}
                        variant="light"
                        size="sm"
                        leftSection={attempt.isResolved ? <IconCheck size={12} /> : <IconX size={12} />}
                      >
                        {attempt.isResolved ? 'Resolved' : 'Unresolved'}
                      </Badge>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {getTimeAgo(attempt.attemptedAt)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {new Date(attempt.attemptedAt).toLocaleString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {attempt.isResolved ? (
                      <Stack gap="xs">
                        <Text size="sm" c="dimmed">
                          {attempt.resolvedAt ? getTimeAgo(attempt.resolvedAt) : 'N/A'}
                        </Text>
                        <Text size="xs" c="dimmed">
                          by {formatResolvedBy(attempt.resolvedBy)}
                        </Text>
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">
                        Not resolved
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label="View details">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => onView(attempt)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      {!attempt.isResolved && (
                        <>
                          <Tooltip label="Unlock account">
                            <ActionIcon
                              variant="subtle"
                              color="green"
                              onClick={() => handleUnlockClick(attempt, 'account')}
                            >
                              <IconLockOpen size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Unlock IP">
                            <ActionIcon
                              variant="subtle"
                              color="orange"
                              onClick={() => handleUnlockClick(attempt, 'ip')}
                            >
                              <IconShield size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })
          )}
        </Table.Tbody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination
            value={currentPage}
            onChange={setCurrentPage}
            total={totalPages}
            size="sm"
          />
        </Group>
      )}

      {/* Unlock Confirmation Modal */}
      <Modal
        opened={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        title={`Unlock ${unlockType === 'account' ? 'Account' : 'IP Address'}`}
        centered
      >
        <Stack gap="md">
          <Alert
            icon={<IconLockOpen size={16} />}
            title={`Unlock ${unlockType === 'account' ? 'Account' : 'IP Address'}`}
            color="blue"
            variant="light"
          >
            This will unlock the {unlockType === 'account' ? 'account' : 'IP address'} and allow future login attempts.
          </Alert>
          
          {selectedAttempt && (
            <Box>
              <Text size="sm" fw={500}>Details:</Text>
              <Text size="sm" c="dimmed">
                <strong>Email:</strong> {selectedAttempt.email}
              </Text>
              <Text size="sm" c="dimmed">
                <strong>IP Address:</strong> {selectedAttempt.ipAddress}
              </Text>
              <Text size="sm" c="dimmed">
                <strong>Attempted:</strong> {getTimeAgo(selectedAttempt.attemptedAt)}
              </Text>
            </Box>
          )}

          <Group justify="flex-end" gap="sm">
            <Button
              variant="outline"
              onClick={() => setUnlockModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleUnlockConfirm}
            >
              Unlock {unlockType === 'account' ? 'Account' : 'IP'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
